import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { rememberedPlaceSlug, useGymAdmin } from '../context/gym-admin-context';
import { isSectionSegment } from '../nav';
import { placePath, sectionRoot, splitPlacePath } from '../place-path';

/**
 * The route under `/:placeSlug`. Lets the pages through when the slug names a
 * place the user manages, and otherwise works out where they meant to go:
 *
 * - `/info`, `/members/9/edit` — a link from before the place was in the URL
 *   (a bookmark, an email already sent). The first segment is a section, so
 *   the whole path is kept and the place they last managed is put in front.
 * - `/somewhere-else/rounds/12` — a place that is not theirs. Same section at
 *   the remembered place, without the record: it was the other place's.
 *
 * Nothing renders with an unknown slug, which is what lets everything under
 * the layout treat `selectedGymSlug` as set.
 */
export function PlaceGate() {
  const { placeSlug = '' } = useParams();
  const { pathname, search } = useLocation();
  const { staffGyms, selectedGymSlug } = useGymAdmin();

  if (selectedGymSlug) return <Outlet />;

  const fallback = rememberedPlaceSlug(staffGyms);
  if (!fallback) return null;

  if (isSectionSegment(placeSlug)) {
    return <Navigate to={{ pathname: placePath(fallback, pathname), search }} replace />;
  }

  return <Navigate to={placePath(fallback, sectionRoot(splitPlacePath(pathname).sectionPath))} replace />;
}

/** The bare root: on to the remembered place, whose index picks the section. */
export function EnterPanel() {
  const { staffGyms } = useGymAdmin();
  const slug = rememberedPlaceSlug(staffGyms);

  return slug ? <Navigate to={placePath(slug, '')} replace /> : null;
}
